
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ContactDetailModal } from "./ContactDetailModal";
import { ContactUserBridge } from "./ContactUserBridge";
import { CopyInvitationLinkButton } from "./CopyInvitationLinkButton";
import { PermanentDeleteModal } from "./PermanentDeleteModal";
import { Building2, Mail, Phone, User, Edit, MapPin, Calendar, UserCheck, FileText, Trash2, Archive, RotateCcw, Skull } from "lucide-react";
import { useState } from "react";
import { ContactStatusBadge } from "./ContactStatusBadge";
import { useInvitationPermissions } from "@/hooks/use-invitation-permissions";
import { useDeleteContact } from "@/hooks/use-contacts";
import { useContactInvitationStatus } from "@/hooks/use-contact-invitation-status";
import { useArchiveUser } from "@/hooks/use-archive-user";
import { useSimpleRole } from "@/hooks/use-simple-role";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";

interface ContactCardProps {
  contact: any;
  onEdit: (contact: any) => void;
}

export const ContactCard = ({ contact, onEdit }: ContactCardProps) => {
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [permanentDeleteOpen, setPermanentDeleteOpen] = useState(false);
  const { data: permissions } = useInvitationPermissions();
  const deleteContact = useDeleteContact();
  const { data: invitationStatus } = useContactInvitationStatus(contact.email, contact.organization_id);
  const { archiveUser, restoreUser } = useArchiveUser();
  const { role: userRole } = useSimpleRole();
  
  const canArchive = userRole === 'superuser' || userRole === 'admin';
  const canPermanentlyDelete = userRole === 'superuser';
  const isArchived = contact.archived === true;
  const hasUserAccount = !!contact.linked_user?.id;

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getContactTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'buyer':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'influencer':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'industry expert':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getOwnerInfo = () => {
    if (contact.internal_owner) {
      return `${contact.internal_owner.first_name} ${contact.internal_owner.last_name} (Staff)`;
    }
    if (contact.external_owner) {
      return `${contact.external_owner.first_name} ${contact.external_owner.last_name} (Partner)`;
    }
    return null;
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${contact.first_name} ${contact.last_name}?`)) {
      deleteContact.mutate(contact.id);
    }
  };

  const handleArchive = () => {
    archiveUser.mutate({
      contactId: contact.id,
      userId: contact.linked_user?.id || null,
      reason: archiveReason || undefined
    });
    setArchiveReason("");
  };

  const handleRestore = () => {
    restoreUser.mutate({
      contactId: contact.id,
      userId: contact.linked_user?.id || null
    });
  };

  return (
    <>
      <Card className={`group hover:shadow-lg transition-all duration-300 overflow-hidden hover:-translate-y-1 bg-white ${isArchived ? 'opacity-60 border-dashed border-slate-300' : ''}`}>
        {/* Header without teal gradient */}
        <div className={`h-1 ${isArchived ? 'bg-gradient-to-r from-slate-400 to-slate-500' : 'bg-gradient-to-r from-slate-200 to-slate-300'} shadow-sm`}></div>
        
        <CardHeader className="pb-4 bg-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
            <Avatar className="h-14 w-14 border shadow-sm ring-1" style={{ borderColor: "black", boxShadow: "0 0 0 1px black" }}>
              <AvatarImage src={contact.avatar_url} className="object-cover" />
              <AvatarFallback className="bg-white text-black font-bold text-lg">
                {getInitials(contact.first_name, contact.last_name)}
              </AvatarFallback>
            </Avatar>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-lg text-slate-900 group-hover:text-slate-700 transition-colors duration-200 truncate">
                  {contact.first_name} {contact.last_name}
                </h3>
                <p className="text-sm font-medium text-slate-600 truncate mt-0.5">{contact.job_title}</p>
              </div>
            </div>
            <div className="flex gap-1 transition-all duration-200">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(contact)}
                className="hover:bg-slate-50 hover:text-slate-700 hover:scale-105 transition-all duration-200"
              >
                <Edit className="h-4 w-4" />
              </Button>
              
              {/* Archive/Restore Button */}
              {canArchive && (
                isArchived ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRestore}
                      className="hover:bg-green-50 hover:text-green-600 hover:scale-105 transition-all duration-200"
                      disabled={restoreUser.isPending}
                      title="Restore Contact"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    
                    {/* Permanent Delete - Superuser only, archived users with accounts only */}
                    {canPermanentlyDelete && hasUserAccount && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPermanentDeleteOpen(true)}
                        className="hover:bg-red-100 hover:text-red-700 hover:scale-105 transition-all duration-200"
                        title="Permanently Delete User Data"
                      >
                        <Skull className="h-4 w-4" />
                      </Button>
                    )}
                  </>
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="hover:bg-amber-50 hover:text-amber-600 hover:scale-105 transition-all duration-200"
                        title="Archive Contact"
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Archive Contact</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to archive {contact.first_name} {contact.last_name}?
                          {contact.has_user_account && (
                            <span className="block mt-2 text-amber-600 font-medium">
                              This user has an active account. Archiving will disable their login access.
                            </span>
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="py-2">
                        <Textarea
                          placeholder="Optional: Reason for archiving..."
                          value={archiveReason}
                          onChange={(e) => setArchiveReason(e.target.value)}
                          className="resize-none"
                          rows={2}
                        />
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleArchive}
                          disabled={archiveUser.isPending}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          {archiveUser.isPending ? "Archiving..." : "Archive"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                className="hover:bg-red-50 hover:text-red-600 hover:scale-105 transition-all duration-200"
                disabled={deleteContact.isPending}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0 pb-5">
          {/* Enhanced badges section */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2 flex-wrap">
              {isArchived && (
                <Badge className="text-xs font-semibold px-3 py-1 rounded-full bg-slate-500 text-white">
                  <Archive className="h-3 w-3 mr-1" />
                  Archived
                </Badge>
              )}
              {contact.partner_ownership_status === 'pending' && (
                <Badge className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                  Pending Approval
                </Badge>
              )}
              {contact.partner_ownership_status === 'rejected' && (
                <Badge className="text-xs font-semibold px-3 py-1 rounded-full bg-red-100 text-red-800 border border-red-300">
                  Ownership Rejected
                </Badge>
              )}
              <Badge 
                className="text-xs font-semibold px-3 py-1 rounded-full shadow-sm text-black"
                style={{ backgroundColor: "white" }}
              >
                {contact.contact_type}
              </Badge>
              <ContactStatusBadge 
                relationshipStatus={contact.relationship_status || 'Prospect'} 
                customerSince={contact.relationship_status === 'Customer' ? null : contact.customer_since}
              />
            </div>
            
            {/* Meta information moved to top right */}
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Calendar className="h-3 w-3" />
              <span>Created {formatDate(contact.created_at)}</span>
            </div>
          </div>

          {/* Two-column layout for contact information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-5">
            {/* Left column - Primary contact info */}
            <div className="space-y-3">
              {contact.email && (
                <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                  <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                    <Mail className="h-4 w-4 text-black" />
                  </div>
                  <span className="truncate font-medium">{contact.email}</span>
                </div>
              )}
              
              {contact.phone && (
                <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                  <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                    <Phone className="h-4 w-4 text-black" />
                  </div>
                  <span className="truncate font-medium">{contact.phone}</span>
                </div>
              )}
            </div>

            {/* Right column - Organization and location */}
            <div className="space-y-3">
              {contact.organizations?.name && (
                <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                  <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                    <Building2 className="h-4 w-4 text-black" />
                  </div>
                  <span className="truncate font-medium">{contact.organizations.name}</span>
                </div>
              )}

              {contact.country && (
                <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                  <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                    <MapPin className="h-4 w-4 text-black" />
                  </div>
                  <span className="truncate font-medium">{contact.country}</span>
                </div>
              )}
            </div>
          </div>

          {/* Secondary information section - now in horizontal layout */}
          {(getOwnerInfo() || contact.notes) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 py-3 border-t border-slate-100 mb-4">
              {getOwnerInfo() && (
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <User className="h-4 w-4 text-slate-400" />
                  <span className="truncate">Owner: {getOwnerInfo()}</span>
                </div>
              )}

              {contact.notes && (
                <div className="flex items-start gap-3 text-sm text-slate-600">
                  <FileText className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <span className="text-xs leading-relaxed line-clamp-2">{contact.notes}</span>
                </div>
              )}
            </div>
          )}

          {/* Enhanced action buttons */}
          <div className="space-y-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDetailModalOpen(true)}
              className="w-full border-slate-300 text-slate-700 hover:shadow-sm transition-all duration-200 font-semibold bg-white"
              style={{ borderColor: "black", borderWidth: "0.25px" }}
            >
              <User className="h-4 w-4 mr-2" />
              View Details
            </Button>
            
            {contact.has_user_account ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full font-semibold cursor-default text-black"
                style={{ backgroundColor: "#DFEF97", borderColor: "black" }}
                disabled
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Active User
              </Button>
            ) : (
              <>
                {invitationStatus?.isExpired ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full font-semibold cursor-default text-red-600 border-red-600 bg-white hover:bg-white disabled:opacity-100"
                    disabled
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Invitation Expired
                  </Button>
                ) : invitationStatus?.hasPendingInvitation ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full font-semibold cursor-default text-green-500 border-green-500 bg-white hover:bg-white disabled:opacity-100"
                    disabled
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    Invitation Pending
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full font-semibold cursor-default text-amber-600 border-amber-600 bg-white hover:bg-white disabled:opacity-100"
                    disabled
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Not Invited
                  </Button>
                )}
                {permissions?.canSendInvitations && (
                  <div className="w-full flex items-center gap-2">
                    <div className="flex-1">
                      <ContactUserBridge 
                        contact={contact} 
                        hasPendingInvitation={invitationStatus?.hasPendingInvitation || false}
                      />
                    </div>
                    <CopyInvitationLinkButton contact={contact} />
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <ContactDetailModal
        contact={contact}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        onEdit={onEdit}
      />
      
      {/* Permanent Delete Modal */}
      {canPermanentlyDelete && hasUserAccount && (
        <PermanentDeleteModal
          open={permanentDeleteOpen}
          onOpenChange={setPermanentDeleteOpen}
          contact={contact}
        />
      )}
    </>
  );
};
